import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { getDossier } from "../../api/dossier/getDossier";
import { getListQuote } from "../../api/quote/getListQuote";
import { getQuoteVehicleSuggestions } from "../../api/quote/getQuoteVehicleSuggestions";
import AITermsText from "../../components/AITermsText";
import APContactHelpButton from "../../components/buttons/APContactHelpButton";
import BackButton from "../../components/buttons/BackButton";
import InfoButton from "../../components/buttons/InfoButton";
import OverwriteQuoteModal from "../../components/modals/OverwriteQuoteModal";
import PageContainer from "../../components/PageContainer";
import PoweredByGemini from "../../components/PoweredByGemini";
import RestrictedAppScope from "../../components/RestrictedAppScope";
import CopyExternalUrlField from "../../components/utils/CopyExternalUrlField";
import { buildQuoteQueryString } from "../../helpers/quoteParams";
import { useFaqs } from "../../hooks/useFaqs";
import { useQuoteLimit } from "../../hooks/useQuoteLimit";
import { useAuth } from "../../providers/AuthProvider";
import { VehicleSuggestion } from "../../types/Vehicles";
import VehicleSuggestionCard from "../components/VehicleSuggestionCard";

const ViewQuoteVehicleSuggestions = () => {
  const { quoteId } = useParams();
  const [selectedSuggestion, setSelectedSuggestion] =
    useState<VehicleSuggestion | null>(null);
  const { getScopedPageUrl } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const makeIdOverride = searchParams.get("makeId") ?? undefined;
  const modelIdOverride = searchParams.get("modelId") ?? undefined;
  const modelYearOverride = searchParams.get("modelYear") ?? undefined;
  const variantIdOverride = searchParams.get("variantId") ?? undefined;
  const editQuote =
    !!searchParams.get("editQuote") && searchParams.get("editQuote") === "true";
  const bevOnly =
    !!searchParams.get("bevOnly") && searchParams.get("bevOnly") === "true";

  if (!quoteId) {
    // TODO: improve client side error messaging
    throw new Error("quote id param is misisng");
  }

  const suggestions = getQuoteVehicleSuggestions(
    quoteId,
    makeIdOverride,
    modelIdOverride,
    modelYearOverride,
    variantIdOverride,
    bevOnly
  );
  const quote = getListQuote(quoteId);
  const dossierId = quote.data?.identifiers.crmDossierId;
  const dossier = getDossier(dossierId || "", { disabled: !dossierId });

  const {
    faqProps,
    isLoading: isLoadingFaqs,
    isSuccess: isSuccessFaqs,
  } = useFaqs({
    pageId: "vehicle-suggestions",
    quoteId: quoteId,
    contextIds: {
      quoteId: quoteId,
      opportunityId: dossier.data?.opportunity?.id,
    },
  });

  // Use quote limit hook
  const {
    showOverwriteModal,
    handleQuoteAction,
    handleOverwriteConfirm,
    handleOverwriteCancel,
    oldestQuote,
  } = useQuoteLimit({
    dossierId: dossierId || "",
    primaryQuoteId: dossier.data?.opportunity?.primaryQuoteId || null,
    onRedirect: (overwriteQuoteId?: string) => {
      if (suggestions.data && selectedSuggestion) {
        const {
          match: { make, model, year },
        } = selectedSuggestion;

        // Build quote parameters using centralized helper
        const quoteParams = {
          aiQueryId: suggestions.data.queryId,
          makeId: make?.id,
          modelId: model?.id,
          modelYear: year,
          overwriteQuote: overwriteQuoteId,
        };

        const queryString = buildQuoteQueryString(quoteParams);

        if (editQuote) {
          navigate(`${getScopedPageUrl("editQuote", quoteId)}${queryString}`);
        } else {
          navigate(`${getScopedPageUrl("cloneQuote", quoteId)}${queryString}`);
        }
      }
    },
    onCancel: () => {
      // Just close the modal, don't redirect
      setSelectedSuggestion(null);
    },
  });

  useEffect(() => {
    if (selectedSuggestion) {
      handleQuoteAction(editQuote);
    }
  }, [selectedSuggestion]);

  if (suggestions.isError) {
    throw suggestions.error;
  }

  if (quote.isError) {
    throw quote.error;
  }

  if (dossier.isError) {
    throw dossier.error;
  }

  const onQuote = (suggestion: VehicleSuggestion) => {
    setSelectedSuggestion(suggestion);
  };

  const renderSuggestionCard = (
    suggestion: VehicleSuggestion,
    index: number
  ) => {
    return (
      <VehicleSuggestionCard
        key={index}
        vehicleSuggestion={suggestion}
        onButtonClick={onQuote}
      />
    );
  };

  const isLoading =
    suggestions.isFetching ||
    isLoadingFaqs ||
    quote.isFetching ||
    dossier.isFetching;
  const isSuccess =
    suggestions.isSuccess &&
    isSuccessFaqs &&
    quote.isSuccess &&
    dossier.isSuccess;

  const renderAppLocation = faqProps.renderAppLocation;

  return (
    <>
      <OverwriteQuoteModal
        isOpen={showOverwriteModal}
        onClose={handleOverwriteCancel}
        onConfirm={handleOverwriteConfirm}
        oldestQuote={oldestQuote}
      />
      <PageContainer
        title="AI Vehicle Suggestions"
        loading={isLoading}
        titleButton={
          isSuccess ? (
            <Box mr={1}>
              {renderAppLocation(
                "AIVehicleSuggestion-Page",
                <InfoButton />,
                true,
                true
              )}
            </Box>
          ) : undefined
        }
      >
        {isSuccess && (
          <Stack spacing={2}>
            <RestrictedAppScope scope="internal">
              <Box>
                <CopyExternalUrlField text="Copy the URL below to share these suggestions (this is a driver specific url):" />
              </Box>
            </RestrictedAppScope>

            {renderAppLocation(
              "AIVehicleSuggestion-PoweredBy",
              <PoweredByGemini />,
              false,
              true
            )}
            <Stack
              sx={{
                display: "grid",
                gap: "16px",
                gridTemplateColumns: { md: "1fr 1fr" },
              }}
            >
              {suggestions.data.suggestions.map(renderSuggestionCard)}
            </Stack>

            <Box sx={{ display: "flex", justifyContent: "start", gap: 2 }}>
              <Box>
                <BackButton />
              </Box>
              <RestrictedAppScope scope="external">
                <Box>
                  <APContactHelpButton
                    regardingOpportunityId={dossier.data.opportunity?.id}
                  />
                </Box>
              </RestrictedAppScope>
            </Box>
            <Box>
              <AITermsText subject="Vehicle Suggestions" />
            </Box>
          </Stack>
        )}
      </PageContainer>
    </>
  );
};

export default ViewQuoteVehicleSuggestions;
